/* eslint-disable func-style */
/* eslint-disable prettier/prettier */
import { showLoading, hideLoading } from 'react-redux-loading-bar';
import moment from 'moment-timezone';
import pluck from 'ramda/src/pluck';
import reject from 'ramda/src/reject';
import isEmpty from 'ramda/src/isEmpty';
import pipe from 'ramda/src/pipe';
import uniqBy from 'ramda/src/uniqBy';
import get from 'lodash/get';
import set from 'lodash/set';
import assign from 'lodash/assign';
import download from 'downloadjs';
import * as types from './constants';
import { LOADING_STARTED, SET_REPORT_APPOINTMENTS_LOADED } from '../constants';
import Http from '../../http';
import HttpReports from '../../httpReports';
import { formatDateForShop } from '../../common/helpers/times';
import config from '../../../config';
import { downloadCsv, downloadExcel, downloadZip } from './helpers';
import { t } from '../../../modules/infra/i18n';
import {
  ChurnZeroService,
  RUN_APPOINTMENTS_REPORT,
  RUN_DEPOSITS_REPORT,
} from '../../../modules/infra/observability/churnzero';

export const setBarberFilterAction = (payload) => ({
  type: types.SET_BARBER_FILTER,
  payload,
});

export const fetchBarbersForFilterAction = (payload) => ({
  type: types.FETCH_BARBERS_FOR_FILTER,
  payload,
});

function getBarberReports(endpoint, query) {
  return (dispatch) =>
    HttpReports.get(endpoint, query).then((reports) => {
      dispatch({ type: types.GET_BARBER_REPORTS, reports });
      const payload = reject(isEmpty)(pluck('barber', reports));
      dispatch(fetchBarbersForFilterAction(payload));
      dispatch(setBarberFilterAction(payload));
      return reports;
    });
}

export function getBarberUserReports(barberId, shopId, fromDate, toDate, serviceCategories) {
  const endpoint = `/shop/${shopId}/report/barber/${fromDate}/${toDate}`;
  return getBarberReports(endpoint, { groupByBarber: true, barberId, serviceCategories });
}

export function getBarberReportsCsv({ shopId, shopName, barberId, fromDate, toDate, barberFilter = [] }) {
  const endpoint = `/shop/${shopId}/report/barber/${fromDate}/${toDate}`;

  return HttpReports.getRaw(endpoint, {
    groupByBarber: true,
    barberId,
    view: 'csv',
    barberFilter,
  }).then((response) =>
    downloadCsv(
      response,
      `${shopName} - ${t('reports:sale_services_report.csv_filename')} ${formatDateForShop(fromDate)} ${t('reports:appointments_report.csv_date_separator')} ${formatDateForShop(toDate)}`
    )
  );
}

export function getShopUserReports(shopId, fromDate, toDate, serviceCategories) {
  const endpoint = `/shop/${shopId}/report/barber/${fromDate}/${toDate}`;
  return getBarberReports(endpoint, { groupByBarber: true, serviceCategories });
}

function getBarberReportSummary(endpoint) {
  return (dispatch) =>
    HttpReports.get(endpoint).then((reportSummary) => {
      dispatch({ type: types.GET_BARBER_REPORT_SUMMARY, reportSummary });
      return reportSummary;
    });
}

export function getBarberUserReportSummary(barberId, shopId, fromDate, toDate) {
  const endpoint = `/shop/${shopId}/report/barber/${fromDate}/${toDate}?barberId={${barberId}}`;
  return getBarberReportSummary(endpoint);
}

export function getShopUserReportSummary(shopId, fromDate, toDate) {
  const endpoint = `/shop/${shopId}/report/barber/${fromDate}/${toDate}`;
  return getBarberReportSummary(endpoint);
}

function fetchAppointments(shopId, filters = {}, barberId, skip = 0) {
  assign(filters, {
    shopId,
    skip,
    limit: types.LOAD_REPORT_APPOINTMENTS_PER_PAGE,
    include: ['appointment_service', 'barber', 'customer', 'payment', 'bookedBy'].join(','),
  });

  if (barberId) {
    set(filters, 'barberId', barberId);
  }

  return Http.get('/appointment', filters).then((data) => {
    if (data.length < types.LOAD_REPORT_APPOINTMENTS_PER_PAGE) {
      return data;
    }

    return fetchAppointments(shopId, filters, barberId, skip + types.LOAD_REPORT_APPOINTMENTS_PER_PAGE).then(
      (data1) => [...data, ...data1]
    );
  });
}

export function getAppointments(shopId, filters = {}, barberId, skip = 0) {
  return (dispatch) => {
    dispatch({ type: LOADING_STARTED });
    dispatch(showLoading());
    return fetchAppointments(shopId, filters, barberId, skip)
      .then((data) => {
        dispatch({ type: SET_REPORT_APPOINTMENTS_LOADED });
        dispatch(hideLoading());

        const barbers = pipe(
          pluck('barber'),
          uniqBy((b) => b.id)
        )(data);
        dispatch({ type: types.FETCH_BARBERS_FOR_FILTER, payload: barbers });
        dispatch({ type: types.SET_BARBER_FILTER, payload: barbers });
        dispatch({
          type: types.IMPORT_REPORT_APPOINTMENTS,
          appointments: data,
        });
      })
      .catch(() => {
        dispatch({ type: SET_REPORT_APPOINTMENTS_LOADED });
        dispatch(hideLoading());
      });
  };
}

export function setCSV(csv) {
  return (dispatch) => {
    dispatch({ type: types.SET_CSV, csv });
    return csv;
  };
}

const toISOFormat = (date) => moment(date).format(config.dateFormatISO);
export function selectStartDate(date) {
  return (dispatch) => {
    dispatch({ type: types.SET_REPORTS_START_DATE_ISO, payload: toISOFormat(date) });
    dispatch({ type: types.SET_REPORTS_START_DATE, date });
  };
}

export function selectEndDate(date) {
  return (dispatch) => {
    dispatch({ type: types.SET_REPORTS_END_DATE_ISO, payload: toISOFormat(date) });
    dispatch({ type: types.SET_REPORTS_END_DATE, date });
  };
}

export function resetReports() {
  return (dispatch) => {
    dispatch({ type: types.RESET_REPORTS });
  };
}

export const addBarberFilterReport = (barber) => ({
  type: types.ADD_BARBER_FILTER_REPORT,
  barber: barber.id,
});

export const removeBarberFilterReport = (barber) => ({
  type: types.REMOVE_BARBER_FILTER_REPORT,
  barber: barber.id,
});

export const showAllBarberFiltersReport = (barbers) => ({
  type: types.SHOW_ALL_BARBER_FILTERS_REPORT,
  barbers: barbers.map((barber) => barber.id),
});

export const hideAllBarberFiltersReport = () => ({
  type: types.HIDE_ALL_BARBER_FILTERS_REPORT,
});

const getDepositsReportAction = (report) => ({
  type: types.GET_DEPOSITS_REPORT,
  payload: report,
});

const clearDepositsReport = () => ({
  type: types.CLEAR_DEPOSITS_REPORT,
});

const fetchDepositsReport =
  (endpoint, startDate, endDate, options = {}) =>
  async (dispatch) => {
    const results = await HttpReports.get(
      endpoint,
      {
        startDate,
        endDate,
      },
      options.notify
    );
    dispatch(getDepositsReportAction(results));
    return results;
  };

export const getDepositsReport =
  (endpoint, from, to, options = {}) =>
  async (dispatch) => {
    dispatch(showLoading());
    dispatch(clearDepositsReport());
    try {
      await dispatch(fetchDepositsReport(endpoint, from, to, options));
      ChurnZeroService.track(RUN_DEPOSITS_REPORT);
      return dispatch(hideLoading());
    } catch (e) {
      return dispatch(hideLoading());
    }
  };

export const getShopDepositsReport =
  (shopId, from, to, options = {}) =>
  (dispatch) => {
    const endpoint = `/shop/${shopId}/deposits-report`;
    return dispatch(getDepositsReport(endpoint, from, to, options));
  };

export const getBarberDepositsReport = (barberId, from, to) => (dispatch) => {
  const endpoint = `/barber/${barberId}/deposits-report`;
  return dispatch(getDepositsReport(endpoint, from, to));
};

const clearPayouts = { type: types.CLEAR_PAYOUTS_REPORT };
const fetchPayouts = (payload, shopId) => ({
  type: types.FETCH_PAYOUTS_REPORT,
  payload,
  shopId,
});
const mapPayouts = (data) => ({
  shop: get(data, 'shop'),
  items: get(data, 'barbers'),
  fromDate: get(data, 'fromDate'),
  toDate: get(data, 'toDate'),
});

export function getPayoutReport({ endpoint, paymentFilter = [] }) {
  return (dispatch) =>
    HttpReports.get(endpoint, {
      paymentFilter,
      sortBy: 'toDate:desc',
    }).then((reports) => {
      dispatch({
        type: types.GET_PAYOUT_REPORT_NO_COMMISSION_SPLIT,
        reports,
      });
      return reports;
    });
}

export function getShopPayoutReport({ shopId, fromDate, toDate, paymentFilter = [] }) {
  const endpoint = `/shop/${shopId}/report/payout/${fromDate}/${toDate}`;
  return getPayoutReport({ endpoint, paymentFilter });
}

export const fetchPayoutsReport = (shopId) => (dispatch) => {
  dispatch(clearPayouts);
  return HttpReports.post(`/shop/${shopId}/review-payout`)
    .then(mapPayouts)
    .then((data) => dispatch(fetchPayouts(data, shopId)));
};

export const fetchBarberPayoutsReport = (barberId) => (dispatch) => {
  dispatch(clearPayouts);
  return HttpReports.post(`/barber/${barberId}/review-payout`).then((data) => dispatch(fetchPayouts(data, barberId)));
};

const dispatchLastPayout = (payload, shopId) => ({
  type: types.FETCH_LAST_PAYOUT,
  payload,
  shopId,
});

const dispatchDailyPayroll = (payload, shopId) => ({
  type: types.FETCH_DAILY_PAYROLL_REPORT,
  payload,
  shopId,
});

export const fetchLastPayoutDate = (shopId) => (dispatch) => {
  const params = {
    sortBy: 'toDate:desc',
    limit: 1,
  };
  return HttpReports.get(`/shop/${shopId}/payout`, params).then((data) =>
    dispatch(dispatchLastPayout(get(data, 'rows.[0].createdAt'), shopId))
  );
};

export const fetchDailyPayroll = (shopId) => (dispatch) => {
  const params = {
    sortBy: 'toDate:desc',
    limit: 1,
  };
  return HttpReports.get(`/shop/${shopId}/last-daily-payroll`, params).then((data) =>
    dispatch(dispatchDailyPayroll(data, shopId))
  );
};

export const fetchBarberLastPayoutDate = (barberId) => (dispatch) => {
  const params = {
    sortBy: 'toDate:desc',
    limit: 1,
  };
  return HttpReports.get(`/barber/${barberId}/payout`, params).then((data) =>
    dispatch(dispatchLastPayout(get(data, 'rows.[0].createdAt'), barberId))
  );
};

const DESIRED_WEEKS_INITIAL = 10;
const DESIRED_WEEKS_MORE = 5;
const BUFFER_MULTIPLIER = 1.3; // 30% buffer for variable shop sizes
const MIN_RECORDS = 20;
const MAX_RECORDS = 200;
const BARBER_COUNT_DEFAULT = 5;

const calculateSmartLimit = (estimatedBarberCount, desiredWeeks) => {
  const calculatedLimit = Math.ceil(desiredWeeks * estimatedBarberCount * BUFFER_MULTIPLIER);
  return Math.max(MIN_RECORDS, Math.min(MAX_RECORDS, calculatedLimit));
};

const getEstimatedBarberCount = (getState, shopId) => {
  const state = getState();
  const shops = state.auth?.user?.shops || [];
  const currentShop = shops.find((shop) => shop.id === shopId);
  if (currentShop?.barbers?.length > 0) {
    return currentShop.barbers.length;
  }
  return BARBER_COUNT_DEFAULT;
};

export const fetchPayoutsHistoryAction = (payload) => ({
  type: types.FETCH_PAYOUT_HISTORY,
  payload,
});

export const fetchPayoutsHistoryInitial =
  (shopId, limit = null) =>
  async (dispatch, getState) => {
    const smartLimit = limit || calculateSmartLimit(getEstimatedBarberCount(getState, shopId), DESIRED_WEEKS_INITIAL);
    const data = await HttpReports.get(`/shop/${shopId}/payout`, { skip: 0, limit: smartLimit, sortBy: 'toDate:desc' });
    return dispatch(fetchPayoutsHistoryAction(data.rows));
  };

export const fetchPayoutsHistoryMore =
  (shopId, skip, limit = null) =>
  async (dispatch, getState) => {
    const smartLimit = limit || calculateSmartLimit(getEstimatedBarberCount(getState, shopId), DESIRED_WEEKS_MORE);
    const data = await HttpReports.get(`/shop/${shopId}/payout`, { skip, limit: smartLimit, sortBy: 'toDate:desc' });
    return dispatch({ type: types.APPEND_PAYOUT_HISTORY, payload: data.rows });
  };

export const fetchBarberPayoutsHistoryInitial =
  (barberId, limit = null) =>
  async (dispatch) => {
    // For barber view, we only need records for 1 barber, so use 1 as count
    const smartLimit = limit || calculateSmartLimit(1, DESIRED_WEEKS_INITIAL);
    const data = await HttpReports.get(`/barber/${barberId}/payout`, {
      skip: 0,
      limit: smartLimit,
      sortBy: 'toDate:desc',
    });
    return dispatch(fetchPayoutsHistoryAction(data.rows));
  };

export const fetchBarberPayoutsHistoryMore =
  (barberId, skip, limit = null) =>
  async (dispatch) => {
    // For barber view, we only need records for 1 barber, so use 1 as count
    const smartLimit = limit || calculateSmartLimit(1, DESIRED_WEEKS_MORE);
    const data = await HttpReports.get(`/barber/${barberId}/payout`, {
      skip,
      limit: smartLimit,
      sortBy: 'toDate:desc',
    });
    return dispatch({ type: types.APPEND_PAYOUT_HISTORY, payload: data.rows });
  };

export const fetchBarberPayoutsHistory = (barberId) => async (dispatch) => {
  let hasMore = true;
  let skip = 0;
  const limit = 500;
  let results = [];

  while (hasMore) {
    // eslint-disable-next-line no-await-in-loop
    const data = await HttpReports.get(`/barber/${barberId}/payout`, { skip, limit, sortBy: 'toDate:desc' });
    if (data.count === results.length || data.rows.length === 0) {
      hasMore = false;
    }
    skip = limit + skip;
    results = [...results, ...data.rows];
  }

  return dispatch(fetchPayoutsHistoryAction(results));
};

export const fetchPayoutsHistory =
  (shopId, maxResults = 100) =>
  async (dispatch) => {
    let hasMore = true;
    let skip = 0;
    const limit = 500;
    let results = [];

    while (hasMore) {
      // eslint-disable-next-line no-await-in-loop
      const data = await HttpReports.get(`/shop/${shopId}/payout`, { skip, limit, sortBy: 'toDate:desc' });
      if (data.count === results.length || data.rows.length === 0 || results.length >= maxResults) {
        hasMore = false;
      }
      skip = limit + skip;
      results = [...results, ...data.rows];
    }

    return dispatch(fetchPayoutsHistoryAction(results));
  };

export const processPayout = (shopId) => () => Http.post(`/shop/${shopId}/process-payout`);

const getProductSaleReports =
  (endpoint, query = {}) =>
  (dispatch) =>
    HttpReports.get(endpoint, query).then((reports) => {
      dispatch({ type: types.FETCH_PRODUCT_SALES_REPORT, payload: reports });
      const payload = reject(isEmpty)(pluck('barber', reports));
      dispatch({ type: types.FETCH_BARBERS_FOR_FILTER, payload });
      dispatch({ type: types.SET_BARBER_FILTER, payload });
      return reports;
    });

export const getBarberProductSaleReports = (barberId, shopId, fromDate, toDate, brands) => {
  const endpoint = `/shop/${shopId}/report/product/${fromDate}/${toDate}`;
  return getProductSaleReports(endpoint, { barberId, groupByBarber: true, brands });
};

export const getShopProductSaleReports = (shopId, fromDate, toDate, brands) => {
  const endpoint = `/shop/${shopId}/report/product/${fromDate}/${toDate}`;
  return getProductSaleReports(endpoint, { groupByBarber: true, brands });
};

export function getProductReportSaleCsv({
  shopId,
  shopName,
  barberId,
  fromDate,
  toDate,
  barberFilter = [],
  brands = [],
}) {
  const endpoint = `/shop/${shopId}/report/product/${fromDate}/${toDate}`;

  return HttpReports.getRaw(endpoint, {
    groupByBarber: true,
    barberId,
    view: 'csv',
    barberFilter,
    brands,
  }).then((response) =>
    downloadCsv(
      response,
      `${shopName} - ${t('reports:sale_products_report.csv_filename')} ${formatDateForShop(fromDate)} ${t('reports:appointments_report.csv_date_separator')} ${formatDateForShop(toDate)}`
    )
  );
}

export function getBarberPayoutReport({ barberId, fromDate, toDate, paymentFilter = [] }) {
  const endpoint = `/barber/${barberId}/report/payout/${fromDate}/${toDate}`;
  return getPayoutReport({ endpoint, paymentFilter });
}

export function getBrandPayoutReport({ brandId, fromDate, toDate, paymentFilter = [] }) {
  const endpoint = `/brand/${brandId}/report/payout/${fromDate}/${toDate}`;
  return getPayoutReport({ endpoint, paymentFilter });
}

function getPayoutReportCsv({ endpoint, fileName, paymentFilter }) {
  return HttpReports.getRaw(endpoint, {
    groupByBarber: true,
    view: 'csv',
    paymentFilter,
  }).then((response) => downloadCsv(response, fileName));
}

export function getShopPayoutReportCsv({ shopId, name, fromDate, toDate, paymentFilter = [] }) {
  const endpoint = `/shop/${shopId}/report/payout/${fromDate}/${toDate}`;
  const fileName = `${name} - ${t('reports:payout_report.csv_filename')} ${formatDateForShop(fromDate)} ${t('reports:appointments_report.csv_date_separator')} ${formatDateForShop(toDate)}`;
  return getPayoutReportCsv({
    endpoint,
    fileName,
    paymentFilter,
  });
}

export function getBarberPayoutReportCsv({ barberId, name, fromDate, toDate, paymentFilter = [] }) {
  const endpoint = `/barber/${barberId}/report/payout/${fromDate}/${toDate}`;
  const fileName = `${name} - ${t('reports:payout_report.csv_filename')} ${formatDateForShop(fromDate)} ${t('reports:appointments_report.csv_date_separator')} ${formatDateForShop(toDate)}`;
  return getPayoutReportCsv({
    endpoint,
    fileName,
    paymentFilter,
  });
}

export function getBrandPayoutReportCsv({ brandId, name, fromDate, toDate, paymentFilter = [] }) {
  const endpoint = `/brand/${brandId}/report/payout/${fromDate}/${toDate}`;
  const fileName = `${name} - ${t('reports:payout_report.csv_filename')} ${formatDateForShop(fromDate)} ${t('reports:appointments_report.csv_date_separator')} ${formatDateForShop(toDate)}`;
  return getPayoutReportCsv({
    endpoint,
    fileName,
    paymentFilter,
  });
}
export const updatePaymentFilter = (payload) => ({
  type: types.UPDATE_PAYMENT_FILTER,
  payload,
});

export const updateStatusFilter = (payload) => ({
  type: types.UPDATE_STATUS_FILTER,
  payload,
});

export const updateAppointmentStatusFilter = (payload) => ({
  type: types.UPDATE_APPOINTMENT_STATUS_FILTER,
  payload,
});

export const updateAppointmentBookedByFilter = (payload) => ({
  type: types.UPDATE_APPOINTMENT_BOOKED_BY_FILTER,
  payload,
});

export const updateAppointmentPlatformFilter = (payload) => ({
  type: types.UPDATE_APPOINTMENT_PLATFORM_FILTER,
  payload,
});

export const updateProductInventoryActionFilter = (payload) => ({
  type: types.UPDATE_PRODUCT_INVENTORY_ACTION_FILTER,
  payload,
});

export const updateBrandFilter = (payload) => ({
  type: types.UPDATE_BRAND_FILTER,
  payload,
});

export const brandFilterSelectAll = () => ({
  type: types.UPDATE_BRAND_FILTER_SELECT_ALL,
});

export const brandFilterDeselectAll = () => ({
  type: types.UPDATE_BRAND_FILTER_DESELECT_ALL,
});

export const updateProductCategoryFilter = (payload) => ({
  type: types.UPDATE_PRODUCT_CATEGORY_FILTER,
  payload,
});

export const productCategoryFilterSelectAll = () => ({
  type: types.UPDATE_PRODUCT_CATEGORY_FILTER_SELECT_ALL,
});

export const productCategoryFilterDeselectAll = () => ({
  type: types.UPDATE_PRODUCT_CATEGORY_FILTER_DESELECT_ALL,
});

export const updateServiceFilter = (payload) => ({
  type: types.UPDATE_SERVICE_FILTER,
  payload,
});

export const shopServicesCount = (payload) => ({
  type: types.FETCH_SERVICES_COUNT,
  payload,
});

export const fetchShopServicesCount = (shopId, fromDate, toDate) => (dispatch) =>
  HttpReports.get(`/shop/${shopId}/report/service/${fromDate}/${toDate}?groupByBarber=true`).then((data) =>
    dispatch(shopServicesCount(data))
  );

export const discountAmount = (payload) => ({
  type: types.FETCH_DISCOUNT_AMOUNT,
  payload,
});

export const fetchShopDiscountAmount = (shopId, fromDate, toDate) => (dispatch) =>
  HttpReports.get(`/shop/${shopId}/report/refund/${fromDate}/${toDate}?groupByBarber=true`).then((data) =>
    dispatch(discountAmount(data))
  );

export const exportDepositsReport = (shopId, startDate, endDate, timezone) => {
  const data = {
    startDate: moment.tz(startDate, config.dateFormat, timezone).startOf('day').toJSON(),
    endDate: moment.tz(endDate, config.dateFormat, timezone).endOf('day').toJSON(),
    view: 'csv',
  };

  return HttpReports.getRaw(`/shop/${shopId}/deposits-report`, data).then((response) =>
    // eslint-disable-next-line max-len
    downloadCsv(response, `${t('reports:deposits_report.csv_filename')} ${formatDateForShop(data.startDate)} - ${formatDateForShop(data.endDate)}`)
  );
};

export const exportBarberDepositsReport = (barberId, startDate, endDate, timezone) => {
  const data = {
    startDate: moment.tz(startDate, config.dateFormat, timezone).startOf('day').toJSON(),
    endDate: moment.tz(endDate, config.dateFormat, timezone).endOf('day').toJSON(),
    view: 'csv',
  };

  return HttpReports.getRaw(`/barber/${barberId}/deposits-report`, data).then((response) =>
    // eslint-disable-next-line max-len
    downloadCsv(response, `${t('reports:deposits_report.csv_filename')} ${formatDateForShop(data.startDate)} - ${formatDateForShop(data.endDate)}`)
  );
};

export const updateCategoryFilter = (category) => ({
  type: types.UPDATE_CATEGORY_FILTER,
  payload: category,
});

export const categoryFilterSelectAll = () => ({
  type: types.CATEGORY_FILTER_SELECT_ALL,
});

export const categoryFilterDeselectAll = () => ({
  type: types.CATEGORY_FILTER_DESELECT_ALL,
});

export function getLoanRepaymentReport(shopId) {
  const endpoint = `/shop/${shopId}/report/loan`;
  return (dispatch) =>
    HttpReports.get(endpoint).then((reports) => {
      dispatch({
        type: types.GET_LOAN_REPAYMENT_REPORT,
        reports,
      });
      return reports;
    });
}

export function getLoanRepaymentReportCsv(shopId) {
  const endpoint = `/shop/${shopId}/report/loan`;
  return HttpReports.getRaw(endpoint, {
    view: 'csv',
  }).then((response) => downloadCsv(response, t('reports:loans_report.csv_filename'), 'text/plain'));
}

export function getAppointmentsReport(ownerId, ownerType, filters, timezone) {
  const fromDate = moment.tz(filters.startDate, config.dateFormat, timezone).startOf('day').toJSON();
  const toDate = moment.tz(filters.endDate, config.dateFormat, timezone).endOf('day').toJSON();
  const endpoint = `/${ownerType}/${ownerId}/report/appointment/${fromDate}/${toDate}`;

  return (dispatch) => {
    dispatch({ type: LOADING_STARTED });
    dispatch(showLoading());
    return HttpReports.get(endpoint, filters)
      .then((data) => {
        dispatch({ type: SET_REPORT_APPOINTMENTS_LOADED });
        dispatch({ type: types.SET_BARBER_FILTER, payload: data.barbers });
        dispatch({
          type: types.IMPORT_REPORT_APPOINTMENTS,
          appointments: data.appointments,
        });
        dispatch(hideLoading());
        ChurnZeroService.track(RUN_APPOINTMENTS_REPORT);
      })
      .catch(() => {
        dispatch({ type: SET_REPORT_APPOINTMENTS_LOADED });
        dispatch(hideLoading());
      });
  };
}

export function getAppointmentsReportCsv(ownerId, ownerType, name, filters, timezone) {
  const fromDate = moment.tz(filters.startDate, config.dateFormat, timezone).startOf('day').toJSON();
  const toDate = moment.tz(filters.endDate, config.dateFormat, timezone).endOf('day').toJSON();
  const endpoint = `/${ownerType}/${ownerId}/report/appointment/${fromDate}/${toDate}`;

  return HttpReports.getRaw(endpoint, {
    ...filters,
    view: 'csv',
  }).then((response) =>
    downloadCsv(
      response,
      // eslint-disable-next-line max-len
      `${name} - ${t('reports:appointments_report.csv_filename')} ${formatDateForShop(fromDate)} ${t('reports:appointments_report.csv_date_separator')} ${formatDateForShop(toDate)}`
    )
  );
}

export const setReportFiltersDirty = () => ({
  type: types.SET_REPORT_FILTERS_DIRTY,
});

export const resetFilters = () => ({
  type: types.RESET_FILTERS,
});

const downloadHourlyWageReport = (endpoint, name) =>
  Http.getRaw(endpoint, { view: 'csv' }, true).then((response) => downloadCsv(response, name, '.xlsx'));

export const downloadShopHourlyWageReport = (shopId, from, to) => {
  const endpoint = `/shop/${shopId}/hourly-wage/${from}/${to}`;
  const name = `${t('reports:hourly_wage_report.csv_filename')}_${formatDateForShop(from)}_${formatDateForShop(to)}`;
  return downloadHourlyWageReport(endpoint, name);
};

export const downloadBrandHourlyWageReport = (brandId, from, to) => {
  const endpoint = `/brand/${brandId}/hourly-wage/${from}/${to}`;
  const name = `${t('reports:hourly_wage_report.csv_filename')}_${formatDateForShop(from)}_${formatDateForShop(to)}`;
  return downloadHourlyWageReport(endpoint, name);
};

export const downloadBrandPaylocityReport = (brandId, from, to) => {
  const endpoint = `/brand/${brandId}/paylocity-report/${formatDateForShop(
    from,
    config.dateFormatISO
  )}/${formatDateForShop(to, config.dateFormatISO)}`;
  return Http.getRaw(endpoint, { view: 'csv' }, true).then((response) =>
    // eslint-disable-next-line max-len
    downloadExcel(response, `${t('reports:paylocity_report.csv_filename')}_${formatDateForShop(from)}_${formatDateForShop(to)}`)
  );
};

export const downloadShopTimeTrackReportCsv = (shopId, from, to, timezone, barberFilter = []) => {
  const endpoint = `/shop/${shopId}/report/time-track/${from}/${to}`;

  return Http.getRaw(endpoint, { view: 'csv', barberFilter }, true).then((response) =>
    // eslint-disable-next-line max-len
    downloadExcel(response, `${t('reports:time_track_report.csv_filename')}_${formatDateForShop(from)}_${formatDateForShop(to)}`)
  );
};

export const downloadBrandTimeTrackReportCsv = (brandId, from, to, timezone, barberFilter = []) => {
  const endpoint = `/brand/${brandId}/report/time-track/${from}/${to}`;

  return Http.getRaw(endpoint, { view: 'csv', barberFilter }, true).then((response) =>
    // eslint-disable-next-line max-len
    downloadExcel(response, `${t('reports:time_track_report.csv_filename')}_${formatDateForShop(from)}_${formatDateForShop(to)}`)
  );
};

export const downloadShopAdpImportCsv = (shopId, from, to) => {
  const endpoint = `/shop/${shopId}/hourly-wage/${from}/${to}`;

  return Http.getRaw(endpoint, { view: 'adp' }, true).then((response) =>
    downloadCsv(response, `${t('reports:adp_import.csv_filename')}_${formatDateForShop(from)}_${formatDateForShop(to)}`)
  );
};

export const downloadBrandAdpImportCsv = (brandId, from, to) => {
  const endpoint = `/brand/${brandId}/hourly-wage/${from}/${to}`;

  return Http.getRaw(endpoint, { view: 'adp' }, true).then((response) =>
    downloadZip(response, `${t('reports:adp_import.csv_filename')}_${formatDateForShop(from)}_${formatDateForShop(to)}`)
  );
};

export const resetDateFilters = () => ({
  type: types.RESET_DATE_FILTERS,
});

export const downloadSettlementReportPdf = (criteria) => {
  let url = `/shop/${criteria.shopId}/one-off-report`;
  if (criteria.brandId) {
    url = `/brand/${criteria.brandId}/one-off-report`;
  }
  return Http.postWithRawResponse(url, criteria).then((response) =>
    download(
      response,
      `${t('reports:settlement_report.csv_filename')}_${formatDateForShop(criteria.fromDate)}_${formatDateForShop(criteria.toDate)}.pdf`,
      'application/pdf'
    )
  );
};

export const downloadBrandCashRegisterReportCsv = (brandId, from, to) => {
  const endpoint = `/brand/${brandId}/cash-registers`;

  return Http.getRaw(endpoint, { view: 'csv', include: 'transactions', from, to }).then((response) =>
    // eslint-disable-next-line max-len
    downloadCsv(response, `${t('reports:cash_registers_report.csv_filename')}_${formatDateForShop(from)}_${formatDateForShop(to)}`)
  );
};
